import { fullFormats } from "ajv-formats/dist/formats";
import { useForm } from "react-hook-form";
import { useUiState } from "../../features/ui/useUiState";
import { TSectionRendererProps } from "../../types";
import {
  extractDefaultValues,
  getValidationObjectFromSections,
} from "../../utils/form.utils";
import { customAjvResolver } from "../../validators";
import { errorLogger } from "../../utils";

type UseFormMethodsCallback = (
  names: string[],
  name: string,
  control: string,
  deps: Array<string | number>,
  values?: Record<string, unknown>,
) => void;
type TOptions = {
  onValidated: UseFormMethodsCallback;
  onInvalid: UseFormMethodsCallback;
  onValidationStarted: UseFormMethodsCallback;
};
export const useFormMethods = (
  sections: TSectionRendererProps[],
  options?: TOptions,
) => {
  const { onInvalid, onValidated, onValidationStarted } = options ?? {};
  const { startFieldValidation, stopFieldValidation, setErrorCode } =
    useUiState();
  const rules = getValidationObjectFromSections(sections);
  const defaultValues = extractDefaultValues(sections);
  const methods = useForm({
    reValidateMode: "onBlur",
    mode: "onBlur",
    resolver: customAjvResolver(
      rules as never,
      {
        formats: fullFormats,
        $data: true,
        allErrors: true,
        verbose: true,
      },
      { mode: "async" },
      // OnValidated Callback
      // fields have been validated.
      (names, name, control, deps = [], values = {}) => {
        if (control === "cap" || control === "taxCode") {
          stopFieldValidation(name);
        }
        if (control === "taxCode") {
          if (!names.includes(name) && names.some(n => deps.includes(n))) {
            methods.clearErrors(name as never);
            methods.trigger(name as never).catch(errorLogger);
          }
        }
        if (names.includes("cap") && values) {
          // set cap city and province values according to the returned payload.
          for (const field in values as unknown as Iterable<unknown>) {
            methods.clearErrors(field as never);
            methods.setValue(field as never, values[field] as never);
          }
        }
        if (onValidated) {
          onValidated(names, name, control, deps, values);
        }
      },
      // OnInvalid Callback
      // fields validation failed.
      (names, name, control, errorCode, deps) => {
        if (errorCode !== 0) {
          setErrorCode(errorCode);
        }
        if (control === "cap" || control === "taxCode") {
          stopFieldValidation(name);
        }
        if (control === "taxCode") {
          const state = methods.getFieldState(name as never);
          if (
            state.isTouched &&
            !names.includes(name) &&
            names.some(n => (deps ?? []).includes(n))
          ) {
            methods.clearErrors(name as never);
            methods.trigger(name as never).catch(errorLogger);
          }
        }
        if (control === "cap" && deps) {
          // reset province and city on cap validation failed.
          for (const field of deps) {
            methods.setValue(field as never, "" as never);
          }
        }
        if (onInvalid) {
          onInvalid(names, name, control, deps ?? []);
        }
      },
      // on validation started.
      (control, name) => {
        if (control === "cap" || control === "taxCode") {
          startFieldValidation(name);
        }
        if (onValidationStarted) {
          onValidationStarted([], name, control, []);
        }
      },
    ),
    defaultValues,
  });

  return { methods };
};
